import React, { useEffect, useState } from "react";
import { BMP_Investment } from "../../models/bmp.model";
import { getBMPInvestments } from "../../api/getBMPInvestments";
import { AddInvestmentModal } from "./modals/AddInvestmentModal";
import { responseHero } from "../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { EmptyIcon } from "./icons/EmptyIcon";
import { PlusIcon } from "./icons/PlusIcon";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon";
import { TrashIcon } from "./icons/TrashIcon";
import Decimal from "decimal.js";

interface InvestmentTableProps {
    onBack?: () => void;
}

export const InvestmentTable: React.FC<InvestmentTableProps> = ({ onBack }) => {
    const [data, setData] = useState<BMP_Investment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeletingId, setIsDeletingId] = useState<number | null>(null);

    const fetchData = async () => {
        setIsLoading(true); 
        try {
            const result = await getBMPInvestments();
            const sortedResult = result.sort((a, b) => 
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            setData(sortedResult);
        } catch (error) {
            console.error("Error fetching investment data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAddClick = () => {
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData: any) => {
        try {
            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/investment`, { data: formData });
            toast.success(`Sikeresen létrehoztad a befizetést!`);
            setIsModalOpen(false);
            await fetchData(); 
        } catch (error: any) {
            console.error(error);
            toast.error(`Sikertelen adatrögzítés!`);
        }
    };

    const handleDeleteClick = async (id: number) => {
        const isConfirmed = window.confirm("Biztosan törölni szeretnéd ezt a tételt? Ez a művelet nem vonható vissza.");
        if (!isConfirmed) return;

        setIsDeletingId(id);
        try {
            await responseHero.delete(`${process.env.REACT_APP_API_URL}/bmp/investment/${id}`);
            toast.success("Sikeres törlés!");
            await fetchData(); 
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Hiba történt a törlés során.");
        } finally {
            setIsDeletingId(null);
        }
    };

    // --- Helpers ---
    const toNum = (val: any) => (val ? new Decimal(val).toNumber() : 0);

    const formatMoney = (amount: any, currency: "EUR" | "USD") => {
        return new Intl.NumberFormat("hu-HU", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(toNum(amount));
    };

    const formatDate = (date: string | Date) => {
        return new Date(date).toLocaleDateString("hu-HU", {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateTotal = (key: string) => {
        return data.reduce((acc, curr) => {
            const val = (curr as any)[key] ? new Decimal((curr as any)[key]) : new Decimal(0);
            return acc.plus(val);
        }, new Decimal(0)).toNumber();
    };

    return (
        <div className="min-h-screen bg-back p-6 sm:p-10 font-sans text-dark-blue">
            <div className="max-w-[1400px] mx-auto mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <button 
                        onClick={onBack} 
                        className="flex items-center gap-2 text-sm font-bold text-brown hover:text-dark-brown transition-colors mb-2 uppercase tracking-wide"
                    >
                        <ArrowLeftIcon />
                        Vissza
                    </button>
                    <h1 className="text-3xl font-extrabold text-dark-blue tracking-tight">Befizetések</h1>
                    <p className="text-light-blue mt-1">Itt láthatóak a befizetések, amelyek a programba érkeztek.</p>
                </div>

                <button
                    onClick={handleAddClick}
                    className="group flex items-center justify-center gap-2 bg-brown text-white px-5 py-2.5 rounded-lg shadow-md hover:bg-dark-brown hover:shadow-lg transition-all active:scale-95"
                >
                    <PlusIcon />
                    <span className="font-semibold">Új befizetés</span>
                </button>
            </div>

            <div className="max-w-[1500px] mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-cream">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-cream border-b border-light-brown/20 text-dark-brown">
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider">Dátum</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider">Ügyfél</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right">Bef. (EUR)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right text-light-blue">Bank 0.3% (EUR)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right text-light-blue">Tranz. (EUR)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right">Bef. (USD)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right text-brown">Váltás (USD)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right text-error">Költség (10%)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right text-dark-blue">Nettó (USD)</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right w-24">Művelet</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ultra-light">
                            {isLoading && !isDeletingId ? (
                                <tr>
                                    <td colSpan={10} className="p-12 text-center">
                                        <div className="animate-spin inline-block w-8 h-8 border-4 border-brown border-t-transparent rounded-full" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="p-16 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <EmptyIcon />
                                            <h3 className="text-lg font-semibold text-dark-blue">Nincs befizetés.</h3>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-ultra-light/50 transition-colors group">
                                        <td className="p-4 text-dark-blue font-medium whitespace-nowrap text-xs">{formatDate(item.date)}</td>
                                        <td className="p-4 text-dark-blue font-medium whitespace-nowrap text-xs">{item.user?.name || 'N/A'}</td>
                                        <td className="p-4 text-right text-dark-blue font-mono text-xs">{formatMoney(item.investedEur, 'EUR')}</td>
                                        <td className="p-4 text-right text-light-blue/80 text-[11px] font-mono italic">-{formatMoney(item.zeroPointThreeBankFee, 'EUR')}</td>
                                        <td className="p-4 text-right text-light-blue/80 text-[11px] font-mono italic">-{formatMoney(item.fixTenEuroBankFee, 'EUR')}</td>
                                        <td className="p-4 text-right text-dark-blue font-mono text-xs">{formatMoney(item.investedUsd, 'USD')}</td>
                                        <td className="p-4 text-right text-brown/80 text-[11px] font-mono italic">
                                            {(item as any).usdChangeBankFee ? `-${formatMoney((item as any).usdChangeBankFee, 'USD')}` : '—'}
                                        </td>
                                        <td className="p-4 text-right text-error/80 text-[11px] font-mono italic">
                                            {item.costDeduction ? `-${formatMoney(item.costDeduction, 'USD')}` : '—'}
                                        </td>
                                        <td className="p-4 text-right text-dark-brown font-bold font-mono text-sm bg-ultra-light/10">
                                            {formatMoney(item.netInvestedUsd, 'USD')}
                                        </td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => handleDeleteClick(item.id)}
                                                disabled={isDeletingId === item.id}
                                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-error hover:bg-error/10 disabled:opacity-50"
                                            >
                                                {isDeletingId === item.id ? (
                                                    <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                                ) : (
                                                    <TrashIcon />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        
                        {!isLoading && data.length > 0 && (
                            <tfoot className="bg-ultra-light text-dark-blue border-t-2 border-cream">
                                <tr>
                                    <td className="p-4 font-bold uppercase text-[9px] tracking-wider">Össz.</td>
                                    <td className="p-4"></td>
                                    <td className="p-4 text-right font-bold text-xs">{formatMoney(calculateTotal('investedEur'), 'EUR')}</td>
                                    <td className="p-4 text-right font-bold text-light-blue text-[10px]">{formatMoney(calculateTotal('zeroPointThreeBankFee'), 'EUR')}</td>
                                    <td className="p-4 text-right font-bold text-light-blue text-[10px]">{formatMoney(calculateTotal('fixTenEuroBankFee'), 'EUR')}</td>
                                    <td className="p-4 text-right font-bold text-xs">{formatMoney(calculateTotal('investedUsd'), 'USD')}</td>
                                    <td className="p-4 text-right font-bold text-brown text-[10px]">{formatMoney(calculateTotal('usdChangeBankFee'), 'USD')}</td>
                                    <td className="p-4 text-right font-bold text-error/80 text-[10px]">{formatMoney(calculateTotal('costDeduction'), 'USD')}</td>
                                    <td className="p-4 text-right font-bold text-dark-brown text-base">{formatMoney(calculateTotal('netInvestedUsd'), 'USD')}</td>
                                    <td className="p-4"></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            <AddInvestmentModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSubmit={handleModalSubmit} 
            />
        </div>
    );
};